// baseURL に /api を含む共有クライアントを使用する
// (従来の './api' は baseURL に /api を含まず、通知APIが /api 抜けで404になっていた)
import api from './apiClient';
import axios from 'axios';

// 型定義
export interface NotificationSettings {
  order_notifications: boolean;
  gps_tracking_notifications: boolean;
  shift_change_notifications: boolean;
  system_notifications: boolean;
  app_notifications: boolean;
  email_notifications: boolean;
  sms_notifications: boolean;
  line_notifications: boolean;
  push_notifications: boolean;
  daily_summary: boolean;
  weekly_summary: boolean;
  [key: string]: boolean;
}

export interface LineNotification {
  id: string;
  user_id: string;
  content: string;
  status: 'delivered' | 'failed' | 'pending';
  sent_at: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationRequest {
  recipients: string[];
  message: string;
  type: 'email' | 'sms' | 'line' | 'push' | 'app';
  priority?: 'low' | 'normal' | 'high';
  scheduled_for?: string;
}

export interface NotificationResponse {
  id: string;
  success: boolean;
  message?: string;
  timestamp: string;
}

export const getNotifications = async () => {
  try {
    const response = await api.get('/notifications');
    return response.data;
  } catch (error) {
    console.error('通知の取得に失敗しました:', error);
    throw error;
  }
};

export const updateNotificationStatus = async (notificationId: string, status: string) => {
  try {
    const response = await api.put(`/notifications/${notificationId}`, { status });
    return response.data;
  } catch (error) {
    console.error('通知の更新に失敗しました:', error);
    throw error;
  }
};

export const deleteNotification = async (notificationId: string) => {
  try {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('通知の削除に失敗しました:', error);
    throw error;
  }
};

export const deleteAllNotifications = async () => {
  try {
    const response = await api.delete('/notifications');
    return response.data;
  } catch (error) {
    console.error('全通知の削除に失敗しました:', error);
    throw error;
  }
};

export const getNotificationSettings = async (): Promise<NotificationSettings> => {
  try {
    const response = await api.get('/notifications/settings');
    return response.data;
  } catch (error) {
    console.error('通知設定の取得に失敗:', error);
    throw error;
  }
};

export const updateNotificationSettings = async (settings: Partial<NotificationSettings>): Promise<NotificationSettings> => {
  try {
    const response = await api.put('/notifications/settings', settings);
    return response.data;
  } catch (error) {
    console.error('通知設定の更新に失敗:', error);
    throw error;
  }
};

export const getLineNotificationHistory = async (params: {
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
} = {}): Promise<LineNotification[]> => {
  try {
    const response = await api.get('/notifications/line/history', { params });
    return response.data;
  } catch (error) {
    console.error('LINE通知履歴の取得に失敗:', error);
    throw error;
  }
};

export const sendLineNotification = async (userId: string, message: string): Promise<NotificationResponse> => {
  try {
    const response = await api.post('/notifications/line/send', {
      user_id: userId,
      message
    });
    return response.data;
  } catch (error) {
    console.error('LINE通知の送信に失敗:', error);
    throw error;
  }
};

export const sendBulkNotification = async (request: NotificationRequest): Promise<NotificationResponse> => {
  try {
    const response = await api.post('/notifications/bulk', request);
    return response.data;
  } catch (error) {
    console.error('一括通知の送信に失敗:', error);
    throw error;
  }
};

export const getNotificationTemplates = async (): Promise<Record<string, string>> => {
  try {
    const response = await api.get('/notifications/templates');
    return response.data;
  } catch (error) {
    console.error('通知テンプレートの取得に失敗:', error);
    throw error;
  }
};

export const getLineAddFriendQR = async (): Promise<string> => {
  try {
    const response = await api.get('/notifications/line/qrcode');
    return response.data.url;
  } catch (error) {
    console.error('LINE QRコードの取得に失敗:', error);
    throw error;
  }
};

export const checkLineLinkage = async (userId: string): Promise<boolean> => {
  try {
    const response = await api.get(`/notifications/line/status/${userId}`);
    return response.data.linked;
  } catch (error) {
    console.error('LINE連携状態の確認に失敗:', error);
    return false;
  }
};
